import React, { useCallback, useEffect, useState } from "react";
import { Layout } from "antd";
import { Navigate, Route, Routes } from "react-router-dom";

import { AnalyticsBrowser } from "@june-so/analytics-next";
import SideBar from "./SideBar";
import { ROUTES } from "../../constants";
import Projects from "../projects/Projects";
import Home from "../home/Home";
import MainHeader from "./MainHeader";
import { getServicesList, getUserAccounts } from "./actions";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import Loading from "../../components/Loading";
import { notifyError } from "../../utils/notification";
import NewTestcase from "../testcases/NewTestcase";
import ResultView from "../testcases/result/ResultView";
import TestcaseResultView from "../testcaseCreation/components/testCaseCreatedResult/TestcaseResultView";
import UserProfile from "../profile/UserProfile";
import BillingSummary from "../billingSummary/BillingSummary";
import { getProjects } from "../projects/actions";
import TestCaseInitView from "../projects/project/TestCaseInitView";
import SupportView from "../testcaseCreation/components/supportView/SupportView";
import QAService from "../qaAsService/QAService";
import GeneratedTestCaseListView from "../projects/project/GeneratedTestcaseListView";
import AutomationScriptGenerator from "../automationScriptGenerator/AutomationScriptGenerator";
import { ENTRY_TYPE } from "../../types/common";
import UpcomingFeatureNotice from "../../components/UpcomingFeatureNotice";
import TestExecutionSuit from "../testExecutionSuit/TestExecutionSuit";
import GeneratedTestScript from "../testExecutionSuit/components/GeneratedTestScript";
import { ENABLE_MONITOR_MODE, JUNEIFY_KEY } from "../../configs/app-config";
import IdGeneratorPage from "../idGenerator/IdGeneratorPage";
import IntegrationPage from "../integration/Integration";
import OnboardingGuide from "../onboard/OnboardingGuide";
import MonitoringJobListViewItemView from "../monitor/MonitoringJobListViewItemView";
import AnalyticsView from "../analytics/AnalyticsView";
import TestRecorder from "../automationScriptGenerator/testRecorder/TestRecorder";
import Monitor from "../monitor/Monitor";
import InitializeGuard from "../../components/guards/InitializeGuard";
import SubscriptionGuard from "../../components/guards/SubscriptionGuard";
import BillingPage from "../billing/BillingPage";

const { Header, Content } = Layout;

const Main = () => {
  const dispatch = useAppDispatch();
  const { userId, email } = useAppSelector(({ userData }) => userData);
  const currentAccount = useAppSelector(
    ({ userData }) => userData.selectedAccount
  );
  const [isLoading, setLoading] = useState<boolean>(true);

  // Extract accountId to avoid object reference changes triggering effect
  const accountId = currentAccount?.accountId;

  // ...existing code...

  // Async effect for fetching services list
  const prevAccountId = React.useRef<string | undefined>();
  useEffect(() => {
    let isActive = true;
    const fetchServicesList = async () => {
      try {
        if (accountId && prevAccountId.current !== accountId) {
          prevAccountId.current = accountId;
          await getProjects(accountId, dispatch);
          await getServicesList(accountId, dispatch);
        }
      } catch (e) {
        if (isActive)
          notifyError("Something wen't wrong", "something_went_wrong");
      }
    };
    fetchServicesList();
    return () => {
      isActive = false;
    };
  }, [accountId, dispatch]);

  // Async effect for user accounts
  useEffect(() => {
    let isActive = true;
    const init = async () => {
      try {
        setLoading(true);
        await getUserAccounts(userId, dispatch, currentAccount);
      } catch (e) {
        if (isActive)
          notifyError("Account data getting error.", "get_account_data_error");
      } finally {
        if (isActive) setLoading(false);
      }
    };
    init();
    return () => {
      isActive = false;
    };
  }, [userId, dispatch]);

  // Analytics effect
  useEffect(() => {
    let isActive = true;
    if (JUNEIFY_KEY && email && isActive) {
      const analytics = AnalyticsBrowser.load({ writeKey: JUNEIFY_KEY });
      analytics.identify("USER_ID", { email });
      analytics.track("Playwright scripts validated");
    }
    return () => {
      isActive = false;
    };
  }, [JUNEIFY_KEY, email]);

  if (isLoading) {
    return <Loading plain={false} size="large" />;
  }

  const renderView = (page: string) => {
    const pageRoute = `/${page}`;
    switch (pageRoute) {
      case ROUTES.GET_STARTED:
        return <OnboardingGuide />;
      case ROUTES.HOME:
        return (
          <SubscriptionGuard>
            <Home />
          </SubscriptionGuard>
        );
      case ROUTES.PROJECTS:
        return (
          <SubscriptionGuard>
            <Projects />
          </SubscriptionGuard>
        );
      case ROUTES.MONITOR:
        return (
          <SubscriptionGuard>
            <Monitor />
          </SubscriptionGuard>
        );
      case ROUTES.TESTCASE_CREATION:
        return (
          <SubscriptionGuard>
            <AutomationScriptGenerator entryType={ENTRY_TYPE.RECORD} />
          </SubscriptionGuard>
        );
      case ROUTES.AUTOMATION_SCRIPT_GENERATOR:
        return (
          <SubscriptionGuard>
            <AutomationScriptGenerator entryType={ENTRY_TYPE.MANUAL} />
          </SubscriptionGuard>
        );
      // Profile and Billing pages are accessible even when subscription is blocked
      case ROUTES.PROFILE:
        return <UserProfile />;
      case ROUTES.BILLING:
        return <BillingPage />;
      case ROUTES.BILLING_SUMMARY:
        return <BillingSummary />;
      case ROUTES.SUPPORT_VIEW:
        return <SupportView />;
      case ROUTES.QA_SERVICE:
        return (
          <SubscriptionGuard>
            <QAService />
          </SubscriptionGuard>
        );
      case ROUTES.ID_GENERATOR:
        return (
          <SubscriptionGuard>
            <IdGeneratorPage />
          </SubscriptionGuard>
        );
      case ROUTES.INTEGRATION:
        return (
          <SubscriptionGuard>
            <IntegrationPage />
          </SubscriptionGuard>
        );
      case ROUTES.TEST_EXECUTION:
        return (
          <SubscriptionGuard>
            <TestExecutionSuit />
          </SubscriptionGuard>
        );
      case ROUTES.REPORTING:
        return (
          <SubscriptionGuard>
            <UpcomingFeatureNotice />
          </SubscriptionGuard>
        );
      default:
        return <Navigate to={ROUTES.PROJECTS} replace />;
    }
  };

  return (
    <InitializeGuard>
      <Layout className="fp_pg_hght_full fp_pg_bckgrnd_trnsprnt">
        <SideBar />
        <Layout className="fp_pg_cntnt_bckgrnd">
          <Header className="fp_main_header">
            <MainHeader />
          </Header>
          <Content className="fp_pg_cntnt">
            <Routes>
              {!ENABLE_MONITOR_MODE && (
                <Route
                  path={`${ROUTES.TEST_CASE}/:projectId/:testcaseId`}
                  element={<NewTestcase />}
                />
              )}

              {!ENABLE_MONITOR_MODE && (
                <Route
                  path={`${ROUTES.RECORD_TEST_CASE}/:moduleId/:requirementId`}
                  element={<TestRecorder />}
                />
              )}

              {!ENABLE_MONITOR_MODE && (
                <Route
                  path={`${ROUTES.VIEW_RESULTS}/:projectId/:testcaseId`}
                  element={<ResultView />}
                />
              )}

              {!ENABLE_MONITOR_MODE && (
                <Route
                  path={`${ROUTES.PROJECTS}/:projectId/:moduleId`}
                  element={<GeneratedTestCaseListView />}
                />
              )}

              {!ENABLE_MONITOR_MODE && (
                <Route
                  path={`${ROUTES.PROJECTS}/:projectId`}
                  element={<TestCaseInitView />}
                />
              )}

              <Route
                path={`${ROUTES.MONITOR}/:monitorId`}
                element={<MonitoringJobListViewItemView />}
              />

              <Route
                path={`${ROUTES.ANALYTICS}/:monitorId`}
                element={<AnalyticsView />}
              />

              {!ENABLE_MONITOR_MODE && (
                <Route
                  path={`${ROUTES.TESTCASE_RESULT}/:moduleId/:requirementId`}
                  element={<TestcaseResultView />}
                />
              )}

              {!ENABLE_MONITOR_MODE && (
                <Route
                  path={`${ROUTES.TEST_EXECUTION}/:testSuiteId`}
                  element={<GeneratedTestScript />}
                />
              )}

              <Route
                path=":page"
                element={<PageWrapper renderView={renderView} />}
              />

              <Route
                path="*"
                element={
                  <Navigate
                    to={ENABLE_MONITOR_MODE ? ROUTES.MONITOR : ROUTES.HOME}
                    replace
                  />
                }
              />
            </Routes>
          </Content>
        </Layout>
      </Layout>
    </InitializeGuard>
  );
};

// Helper wrapper for dynamic page route
const PageWrapper = ({
  renderView,
}: {
  renderView: (page: string) => JSX.Element;
}) => {
  // useParams from react-router-dom v6
  const { page } = require("react-router-dom").useParams();
  return renderView(page);
};

export default Main;
